'use client';

import React, { useContext } from 'react';
import { ArrowDownIcon, ChevronSelectorVerticalIcon } from '@milanote/roma-icons';
import type { ComponentPropsWithRef, CSSProperties, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import type {
    ColumnProps as AriaColumnProps,
    RowProps as AriaRowProps,
    TableHeaderProps as AriaTableHeaderProps,
    TableProps as AriaTableProps,
} from 'react-aria-components';
import {
    Cell as AriaCell,
    Collection as AriaCollection,
    CollectionRendererContext,
    Column as AriaColumn,
    Group as AriaGroup,
    ResizableTableContainer as AriaResizableTableContainer,
    Row as AriaRow,
    Table as AriaTable,
    TableBody as AriaTableBody,
    TableHeader as AriaTableHeader,
} from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { useSortableTable } from './useSortableTable';

const SEMANTIC_NAME = 'RomaAdminDashboardDataTable';

type DataTableSize = 'sm' | 'md';

const DataTableContext = React.createContext<{ size: DataTableSize }>({ size: 'sm' });
const DataTableHeaderContext = React.createContext<{ bordered: boolean }>({ bordered: true });

const sizeStyles = {
    sm: {
        head: 'px-3 py-2 text-xs',
        cell: 'px-3 py-2 text-sm',
        row: 'min-h-9',
    },
    md: {
        head: 'px-3.5 py-2.5 text-xs',
        cell: 'px-3.5 py-3 text-sm',
        row: 'min-h-11',
    },
};

type TableRootProps = Omit<AriaTableProps, 'className' | 'style'> & {
    className?: string;
    disallowTypeAhead?: boolean;
    size?: DataTableSize;
    style?: CSSProperties;
};

const TableRoot = ({ className, size = 'sm', style, ...props }: TableRootProps) => {
    return (
        <DataTableContext.Provider value={{ size }}>
            <div className={cx(SEMANTIC_NAME, 'w-full bg-primary', className)}>
                <AriaResizableTableContainer className="w-full overflow-x-auto" style={style}>
                    <AriaTable {...props} className="w-full border-separate border-spacing-0 bg-primary" />
                </AriaResizableTableContainer>
            </div>
        </DataTableContext.Provider>
    );
};

TableRoot.displayName = `${SEMANTIC_NAME}.Root`;

type DataTableHeaderProps<T extends object> = AriaTableHeaderProps<T> & {
    bordered?: boolean;
};

const DataTableHeader = <T extends object>({
    columns,
    children,
    bordered = true,
    className,
    ...props
}: DataTableHeaderProps<T>) => {
    return (
        <AriaTableHeader
            {...props}
            className={(state) =>
                cx(
                    'bg-secondary',
                    bordered && '[&>tr>th]:border-b [&>tr>th]:border-secondary',
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            <DataTableHeaderContext.Provider value={{ bordered }}>
                <AriaCollection items={columns}>{children}</AriaCollection>
            </DataTableHeaderContext.Provider>
        </AriaTableHeader>
    );
};

DataTableHeader.displayName = `${SEMANTIC_NAME}.Header`;

type DataTableHeadProps = Omit<AriaColumnProps, 'children' | 'className' | 'style' | 'id'> &
    Omit<ThHTMLAttributes<HTMLTableCellElement>, 'children' | 'className' | 'style' | 'id'> & {
        children?: AriaColumnProps['children'];
        id?: AriaColumnProps['id'];
        label?: ReactNode;
        className?: AriaColumnProps['className'];
    };

const DataTableHead = (props: DataTableHeadProps) => {
    const { className, label, children } = props;
    const { size } = useContext(DataTableContext);
    const { bordered } = useContext(DataTableHeaderContext);
    const { isVirtualized } = useContext(CollectionRendererContext);

    return (
        <AriaColumn
            {...props}
            className={(state) =>
                cx(
                    'relative bg-secondary text-left font-semibold whitespace-nowrap text-quaternary outline-hidden',
                    isVirtualized && 'h-[inherit] w-[inherit]',
                    bordered && 'border-b border-secondary',
                    state.allowsSorting && 'cursor-pointer',
                    sizeStyles[size].head,
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            {(state) => (
                <AriaGroup className="flex w-full items-center gap-1">
                    <div className="flex items-center gap-1">
                        {label && (
                            <span className="text-xs font-semibold whitespace-nowrap text-quaternary">{label}</span>
                        )}
                        {typeof children === 'function' ? children(state) : children}
                    </div>

                    {state.allowsSorting &&
                        (state.sortDirection ? (
                            <ArrowDownIcon
                                className={cx(
                                    'size-3 stroke-[3px] text-fg-quaternary',
                                    state.sortDirection === 'ascending' && 'rotate-180',
                                )}
                            />
                        ) : (
                            <ChevronSelectorVerticalIcon size={12} strokeWidth={3} className="text-fg-quaternary" />
                        ))}
                </AriaGroup>
            )}
        </AriaColumn>
    );
};

DataTableHead.displayName = `${SEMANTIC_NAME}.Head`;

type DataTableRowProps<T extends object> = AriaRowProps<T> &
    Omit<ComponentPropsWithRef<'tr'>, 'children' | 'className' | 'onClick' | 'slot' | 'style' | 'id'> & {
        size?: DataTableSize;
    };

const DataTableRow = <T extends object>(props: DataTableRowProps<T>) => {
    const { columns, children, className, size: sizeProp } = props;
    const context = useContext(DataTableContext);
    const { isVirtualized } = useContext(CollectionRendererContext);
    const size = sizeProp ?? context.size;

    return (
        <AriaRow
            {...props}
            className={(state) =>
                cx(
                    'relative outline-focus-ring transition-colors hover:bg-primary_hover',
                    isVirtualized && 'h-[inherit] w-[inherit]',
                    sizeStyles[size].row,
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            <AriaCollection items={columns}>{children}</AriaCollection>
        </AriaRow>
    );
};

DataTableRow.displayName = `${SEMANTIC_NAME}.Row`;

type DataTableCellProps = Omit<TdHTMLAttributes<HTMLTableCellElement>, 'children' | 'className' | 'style' | 'id'> & {
    children?: ReactNode;
    className?: ComponentPropsWithRef<typeof AriaCell>['className'];
    size?: DataTableSize;
};

const DataTableCell = ({ className, size: sizeProp, ...props }: DataTableCellProps) => {
    const context = useContext(DataTableContext);
    const { isVirtualized } = useContext(CollectionRendererContext);
    const size = sizeProp ?? context.size;

    return (
        <AriaCell
            {...props}
            className={(state) =>
                cx(
                    'relative border-b border-secondary text-tertiary outline-focus-ring',
                    isVirtualized && 'h-[inherit] w-[inherit]',
                    sizeStyles[size].cell,
                    typeof className === 'function' ? className(state) : className,
                )
            }
        />
    );
};

DataTableCell.displayName = `${SEMANTIC_NAME}.Cell`;

const DataTable = TableRoot as typeof TableRoot & {
    Body: typeof AriaTableBody;
    Cell: typeof DataTableCell;
    Head: typeof DataTableHead;
    Header: typeof DataTableHeader;
    Row: typeof DataTableRow;
    useSortableTable: typeof useSortableTable;
};

DataTable.Body = AriaTableBody;
DataTable.Cell = DataTableCell;
DataTable.Head = DataTableHead;
DataTable.Header = DataTableHeader;
DataTable.Row = DataTableRow;
DataTable.useSortableTable = useSortableTable;

export { DataTable };
export type { DataTableSize };
